import React from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Divider,
  Button,
  Select,
  Upload,
} from "antd";
import type { UploadProps } from "antd";
import { HeaderOptions, FooterOptions } from "../../../types/dashboard";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;

  title: string;
  subtitle: string;
  setTitle: (v: string) => void;
  setSubtitle: (v: string) => void;

  headerOptions: HeaderOptions;
  setHeaderOptions: (updater: (prev: HeaderOptions) => HeaderOptions) => void;

  footerLeft: string;
  footerRight: string;
  setFooterLeft: (v: string) => void;
  setFooterRight: (v: string) => void;

  footerOptions: FooterOptions;
  setFooterOptions: (updater: (prev: FooterOptions) => FooterOptions) => void;

  logoUploadProps: UploadProps;
  headerImageUploadProps: UploadProps;
  footerImageUploadProps: UploadProps;
};

const PropertiesDrawer: React.FC<Props> = (p) => {
  return (
    <Drawer
      title="Properties"
      placement="right"
      width={420}
      open={p.open}
      onClose={p.onClose}
      destroyOnClose={false}
    >
      <Form
        layout="vertical"
        onSubmitCapture={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          const tag = (e.target as HTMLElement).tagName;
          if (e.key === "Enter" && tag !== "TEXTAREA") {
            e.preventDefault();
          }
        }}
      >
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Header — Text
          </Divider>

          <Form.Item label="Title">
            <Input
              value={p.title}
              onChange={(e) => p.setTitle(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Subtitle">
            <Input
              value={p.subtitle}
              onChange={(e) => p.setSubtitle(e.target.value)}
            />
          </Form.Item>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Font size">
              <InputNumber
                min={10}
                max={72}
                value={p.headerOptions.fontSize}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    fontSize: Number(v || 20),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Font family">
              <Select
                value={p.headerOptions.fontFamily}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    fontFamily: v,
                  }))
                }
                options={[
                  { label: "Poppins", value: "Poppins" },
                  { label: "Arial", value: "Arial" },
                  { label: "Inter", value: "Inter" },
                  { label: "Roboto", value: "Roboto" },
                ]}
              />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Title color">
              <Input
                type="color"
                value={p.headerOptions.titleColor}
                onChange={(e) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    titleColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Subtitle color">
              <Input
                type="color"
                value={p.headerOptions.subtitleColor}
                onChange={(e) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    subtitleColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Header — Spacing
          </Divider>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Padding left">
              <InputNumber
                min={0}
                value={p.headerOptions.paddingLeft}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    paddingLeft: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Padding right">
              <InputNumber
                min={0}
                value={p.headerOptions.paddingRight}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    paddingRight: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Header — Brand
          </Divider>

          <Form.Item label="Upload logo">
            <Upload {...p.logoUploadProps}>
              <Button htmlType="button">Upload logo</Button>
            </Upload>
          </Form.Item>

          <Form.Item label="Logo align">
            <Select
              value={p.headerOptions.logoAlign}
              onChange={(v) =>
                p.setHeaderOptions((o) => ({
                  ...o,
                  logoAlign: v,
                }))
              }
              options={[
                { label: "Left", value: "left" },
                { label: "Right", value: "right" },
              ]}
            />
          </Form.Item>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Header — Image
          </Divider>

          <Form.Item label="Upload header image">
            <Upload {...p.headerImageUploadProps}>
              <Button htmlType="button">Upload header image</Button>
            </Upload>
          </Form.Item>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Align">
              <Select
                value={p.headerOptions.headerImageAlign}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    headerImageAlign: v,
                  }))
                }
                options={[
                  { label: "Left", value: "left" },
                  { label: "Right", value: "right" },
                ]}
              />
            </Form.Item>

            <Form.Item label="Width">
              <InputNumber
                min={40}
                value={p.headerOptions.headerImageWidth}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    headerImageWidth: Number(v || 160),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <Form.Item label="Radius">
            <InputNumber
              min={0}
              value={p.headerOptions.headerImageRadius}
              onChange={(v) =>
                p.setHeaderOptions((o) => ({
                  ...o,
                  headerImageRadius: Number(v || 0),
                }))
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Header — Background & Border
          </Divider>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Background">
              <Input
                type="color"
                value={p.headerOptions.backgroundColor}
                onChange={(e) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    backgroundColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Border color">
              <Input
                type="color"
                value={p.headerOptions.borderColor}
                onChange={(e) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    borderColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Radius">
              <InputNumber
                min={0}
                value={p.headerOptions.radius}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    radius: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Border width">
              <InputNumber
                min={0}
                value={p.headerOptions.borderWidth}
                onChange={(v) =>
                  p.setHeaderOptions((o) => ({
                    ...o,
                    borderWidth: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Footer — Text
          </Divider>

          <Form.Item label="Left text">
            <Input
              value={p.footerLeft}
              onChange={(e) => p.setFooterLeft(e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Right text">
            <Input
              value={p.footerRight}
              onChange={(e) => p.setFooterRight(e.target.value)}
            />
          </Form.Item>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Font size">
              <InputNumber
                min={10}
                max={72}
                value={p.footerOptions.fontSize}
                onChange={(v) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    fontSize: Number(v || 12),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Font family">
              <Select
                value={p.footerOptions.fontFamily}
                onChange={(v) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    fontFamily: v,
                  }))
                }
                options={[
                  { label: "Poppins", value: "Poppins" },
                  { label: "Arial", value: "Arial" },
                  { label: "Inter", value: "Inter" },
                  { label: "Roboto", value: "Roboto" },
                ]}
              />
            </Form.Item>
          </div>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Footer — Image
          </Divider>

          <Form.Item label="Upload footer image">
            <Upload {...p.footerImageUploadProps}>
              <Button htmlType="button">Upload footer image</Button>
            </Upload>
          </Form.Item>

          <Form.Item label="Image align">
            <Select
              value={p.footerOptions.imageAlign}
              onChange={(v) =>
                p.setFooterOptions((o) => ({
                  ...o,
                  imageAlign: v,
                }))
              }
              options={[
                { label: "Left", value: "left" },
                { label: "Right", value: "right" },
              ]}
            />
          </Form.Item>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Text color">
              <Input
                type="color"
                value={p.footerOptions.textColor}
                onChange={(e) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    textColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Background">
              <Input
                type="color"
                value={p.footerOptions.backgroundColor}
                onChange={(e) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    backgroundColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
        </div>
        <div
          style={{
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            padding: "1rem",
            marginTop: "1rem",
          }}
        >
          <Divider
            orientation="left"
            style={{
              color: "#4A1C96",
              fontFamily: "Poppins",
              fontSize: "20px",
              fontWeight: "600",
            }}
          >
            Footer — Spacing & Border
          </Divider>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Padding X">
              <InputNumber
                min={0}
                value={p.footerOptions.paddingX}
                onChange={(v) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    paddingX: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Padding Y">
              <InputNumber
                min={0}
                value={p.footerOptions.paddingY}
                onChange={(v) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    paddingY: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <Form.Item label="Border color">
              <Input
                type="color"
                value={p.footerOptions.borderColor}
                onChange={(e) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    borderColor: e.target.value,
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Border width">
              <InputNumber
                min={0}
                value={p.footerOptions.borderWidth}
                onChange={(v) =>
                  p.setFooterOptions((o) => ({
                    ...o,
                    borderWidth: Number(v || 0),
                  }))
                }
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <Form.Item label="Radius">
            <InputNumber
              min={0}
              value={p.footerOptions.radius}
              onChange={(v) =>
                p.setFooterOptions((o) => ({
                  ...o,
                  radius: Number(v || 0),
                }))
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>
        <Divider />
        <Button
          style={{
            backgroundColor: "#4A1C96",
            border: "1px solid #4A1C96",
            color: "white",
            borderRadius: "8px",
            height: "44px",
            fontFamily: "Poppins",
            fontSize: "14px",
          }}
          block
          htmlType="button"
          onClick={p.onSave}
          loading={p.saving}
        >
          Save changes
        </Button>
      </Form>
    </Drawer>
  );
};

export default React.memo(PropertiesDrawer);
